import { z } from 'zod';
import { AXES } from './bareme.js';
import type { CanalAnnonce } from './formulaire.js';
import type { OrigineAnnonce } from './dossiers.js';
import { construireReference } from './reference.js';

/** Type PostgreSQL `statut_annonce`. */
export const STATUTS_ANNONCE = ['brouillon', 'validee', 'publiee', 'close', 'archivee'] as const;
export const statutAnnonceSchema = z.enum(STATUTS_ANNONCE);
export type StatutAnnonce = z.infer<typeof statutAnnonceSchema>;

export const LIBELLES_STATUT_ANNONCE: Record<StatutAnnonce, string> = {
  brouillon: 'Brouillon',
  validee: 'Validée',
  publiee: 'Publiée',
  close: 'Close',
  archivee: 'Archivée',
};

/**
 * Annonces qui reçoivent des candidatures : ni brouillon (aucune référence,
 * publiée nulle part), ni close, ni archivée. C'est la liste proposée partout
 * où l'on affecte une candidature à une annonce.
 */
export const STATUTS_ANNONCE_OUVERTE: readonly StatutAnnonce[] = ['validee', 'publiee'];
export function annonceOuverte(statut: string): boolean {
  return (STATUTS_ANNONCE_OUVERTE as readonly string[]).includes(statut);
}

/**
 * Annonces auxquelles une candidature REÇUE peut encore se rattacher (file
 * d'ingestion) : une candidature postée la veille d'une clôture arrive après,
 * donc les annonces closes en font partie. Un brouillon (sans référence,
 * publié nulle part) et une archivée n'en sont pas.
 */
export const STATUTS_ANNONCE_RATTACHABLE: readonly StatutAnnonce[] = ['validee', 'publiee', 'close'];
export function annonceRattachable(statut: string): boolean {
  return (STATUTS_ANNONCE_RATTACHABLE as readonly string[]).includes(statut);
}

/** PUT /annonces/:id/cachee — ranger l'annonce hors de SA liste, ou l'y remettre. */
export const masquageAnnonceSchema = z.object({ cachee: z.boolean() }).strict();
export type MasquageAnnonce = z.infer<typeof masquageAnnonceSchema>;

/** Contrats pratiqués en Tunisie. */
export const TYPES_CONTRAT = ['CDI', 'CDD', 'SIVP', 'Karama', 'Stage', 'Saisonnier'] as const;
export type TypeContrat = (typeof TYPES_CONTRAT)[number];

export const LANGUES_ANNONCE = ['fr', 'ar'] as const;
export type LangueAnnonce = (typeof LANGUES_ANNONCE)[number];
export const LIBELLES_LANGUE: Record<LangueAnnonce, string> = { fr: 'Français', ar: 'Arabe' };

/** Plateformes où une annonce se publie (docs/INGESTION_EMAIL.md § Rattachement). */
export const PLATEFORMES = ['tanitjobs', 'tunisietravail', 'keejob', 'direct'] as const;
export type Plateforme = (typeof PLATEFORMES)[number];
export const LIBELLES_PLATEFORME: Record<Plateforme, string> = {
  tanitjobs: 'Tanitjobs',
  tunisietravail: 'Tunisie Travail',
  keejob: 'Keejob',
  direct: 'Direct (site, affichage, réseaux)',
};

const DATE_JOUR = /^\d{4}-\d{2}-\d{2}$/;

export const critereAnnonceSchema = z
  .object({
    type: z.enum(['eliminatoire', 'souhaitable']),
    axe: z.enum(AXES),
    libelle: z.string().trim().min(2).max(200),
  })
  .strict();
export type CritereAnnonce = z.infer<typeof critereAnnonceSchema>;

/** Réponses du formulaire guidé (docs/ARCHITECTURE.md § 5.1). */
export const saisieAnnonceSchema = z
  .object({
    familleId: z.string().uuid(),
    intitule: z.string().trim().min(3).max(160),
    pointsDeVente: z.array(z.string().uuid()).max(20),
    typeContrat: z.enum(TYPES_CONTRAT).nullable(),
    horaires: z.string().trim().max(300).nullable(),
    remuneration: z.string().trim().max(200).nullable(),
    datePriseDePoste: z.string().regex(DATE_JOUR).nullable(),
    missions: z.array(z.string().trim().min(2).max(300)).max(15),
    criteres: z.array(critereAnnonceSchema).max(20),
    langues: z.array(z.enum(LANGUES_ANNONCE)).min(1).max(2),
  })
  .strict();
export type SaisieAnnonce = z.infer<typeof saisieAnnonceSchema>;

export type TextesAnnonce = Partial<Record<LangueAnnonce, string>>;

/** Corps de POST /annonces et PUT /annonces/:id : la saisie et les textes édités. */
export const enregistrementAnnonceSchema = z
  .object({
    saisie: saisieAnnonceSchema,
    textes: z
      .object({ fr: z.string().max(8000).optional(), ar: z.string().max(8000).optional() })
      .strict(),
  })
  .strict();
export type EnregistrementAnnonce = z.infer<typeof enregistrementAnnonceSchema>;

export const publicationSchema = z
  .object({
    plateforme: z.enum(PLATEFORMES),
    offreExterne: z.string().trim().max(40).regex(/^[\w.-]*$/, 'Numéro d’offre : chiffres et lettres').nullable(),
    intitulePublie: z.string().trim().max(200).nullable(),
    url: z
      .string()
      .trim()
      .max(500)
      .url()
      .refine((u) => /^https?:\/\//i.test(u), 'Adresse web en http ou https')
      .nullable(),
    publieeLe: z.string().regex(DATE_JOUR).nullable(),
  })
  .strict();
export type Publication = z.infer<typeof publicationSchema>;

/**
 * Jeton posé dans le texte proposé : la référence n'existe qu'à la validation,
 * qui le remplace. Il est neutre pour tenir dans un texte arabe comme français.
 */
export const JETON_REFERENCE = '{{REFERENCE}}';
export const ADRESSE_CANDIDATURE = 'rh@tiba-invest.com';

const LIBELLE_REFERENCE: Record<LangueAnnonce, string> = { fr: 'Référence', ar: 'المرجع' };

const CONTRATS_AR: Record<TypeContrat, string> = {
  CDI: 'عقد غير محدد المدة (CDI)',
  CDD: 'عقد محدد المدة (CDD)',
  SIVP: 'عقد تربص للإعداد للحياة المهنية (SIVP)',
  Karama: 'عقد الكرامة',
  Stage: 'تربص',
  Saisonnier: 'عمل موسمي',
};

function dateLisible(jour: string): string {
  const [annee, mois, date] = jour.split('-');
  return `${date}/${mois}/${annee}`;
}

/**
 * Proposition de texte à partir du formulaire guidé, que le responsable RH
 * édite ensuite librement. Aucun modèle de langue : le texte ne dit que ce qui
 * a été saisi, et rien d'autre ne s'y invente.
 */
export function proposerTexte(
  saisie: SaisieAnnonce,
  contexte: { pointsDeVente: readonly string[] },
  langue: LangueAnnonce,
): string {
  const lignes: string[] = [];
  const liste = (titre: string, elements: readonly string[]) => {
    if (elements.length > 0) lignes.push('', titre, ...elements.map((e) => `- ${e}`));
  };
  const eliminatoires = saisie.criteres.filter((c) => c.type === 'eliminatoire').map((c) => c.libelle);
  const souhaitables = saisie.criteres.filter((c) => c.type === 'souhaitable').map((c) => c.libelle);
  const lieux = contexte.pointsDeVente.join(', ');

  if (langue === 'fr') {
    lignes.push(`${saisie.intitule} — Plaisirs Gourmands`, '');
    lignes.push(
      lieux
        ? `Plaisirs Gourmands (Tiba Invest) recrute pour : ${lieux}.`
        : 'Plaisirs Gourmands (Tiba Invest) recrute.',
    );
    liste('Missions :', saisie.missions);
    liste('Profil recherché :', eliminatoires);
    liste('Atouts appréciés :', souhaitables);
    liste(
      'Conditions :',
      [
        saisie.typeContrat && `Contrat : ${saisie.typeContrat}`,
        saisie.horaires && `Horaires : ${saisie.horaires}`,
        saisie.remuneration && `Rémunération : ${saisie.remuneration}`,
        saisie.datePriseDePoste && `Prise de poste : ${dateLisible(saisie.datePriseDePoste)}`,
      ].filter((e): e is string => Boolean(e)),
    );
    lignes.push(
      '',
      `Pour postuler, envoyez votre CV à ${ADRESSE_CANDIDATURE} en indiquant la référence ` +
        `${JETON_REFERENCE} dans l’objet du message.`,
      `${LIBELLE_REFERENCE.fr} : ${JETON_REFERENCE}`,
    );
  } else {
    lignes.push(`${saisie.intitule} — Plaisirs Gourmands`, '');
    lignes.push(
      lieux
        ? `تنتدب شركة Tiba Invest (Plaisirs Gourmands) للعمل في: ${lieux}.`
        : 'تنتدب شركة Tiba Invest (Plaisirs Gourmands).',
    );
    liste('المهام:', saisie.missions);
    liste('الشروط المطلوبة:', eliminatoires);
    liste('مؤهلات إضافية:', souhaitables);
    liste(
      'ظروف العمل:',
      [
        saisie.typeContrat && `نوع العقد: ${CONTRATS_AR[saisie.typeContrat]}`,
        saisie.horaires && `أوقات العمل: ${saisie.horaires}`,
        saisie.remuneration && `الأجر: ${saisie.remuneration}`,
        saisie.datePriseDePoste && `تاريخ مباشرة العمل: ${dateLisible(saisie.datePriseDePoste)}`,
      ].filter((e): e is string => Boolean(e)),
    );
    lignes.push(
      '',
      `للترشح، يرجى إرسال السيرة الذاتية إلى ${ADRESSE_CANDIDATURE} مع ذكر المرجع ` +
        `${JETON_REFERENCE} في موضوع الرسالة.`,
      `${LIBELLE_REFERENCE.ar}: ${JETON_REFERENCE}`,
    );
  }
  return lignes.join('\n');
}

/**
 * Remplace le jeton par la référence attribuée. Si le texte édité l'a perdu,
 * la référence est ajoutée à la fin : c'est elle qui rattache les candidatures.
 */
export function appliquerReference(texte: string, reference: string, langue: LangueAnnonce): string {
  const remplace = texte.split(JETON_REFERENCE).join(reference);
  if (remplace.toUpperCase().includes(reference.toUpperCase())) return remplace;
  return `${remplace.trimEnd()}\n\n${LIBELLE_REFERENCE[langue]} : ${reference}`;
}

/** Référence `PG-<CODE>-<AAMMJJ>-<NNN>` à partir du jour calendaire `AAMMJJ`. */
export function referenceDuJour(code: string, aammjj: string, numero: number): string {
  const date = new Date(
    2000 + Number(aammjj.slice(0, 2)),
    Number(aammjj.slice(2, 4)) - 1,
    Number(aammjj.slice(4, 6)),
  );
  return construireReference(code, date, numero);
}

/**
 * Expressions qui feraient d'un critère proscrit une condition d'embauche
 * (CLAUDE.md § Interdits) : âge, situation familiale, sexe, santé, nationalité,
 * religion. Les durées d'expérience (« 10 à 15 ans d'expérience ») et
 * l'écriture inclusive (« Caissier(ère) ») passent. Le français seul est
 * contrôlé : le texte arabe reste à relire.
 */
const PROSCRITS: readonly string[] = [
  'âgée?s?',
  'agée?s?',
  'âge (?:minimum|maximum|limite|requis)',
  "\\d{2}\\s*(?:à|-|et)\\s*\\d{2}\\s*ans(?!\\s*d['’]\\s*exp)",
  "(?:moins|plus) de \\d{2} ans(?!\\s*d['’]\\s*exp)",
  'célibataires?',
  'mariée?s?',
  'sans enfants?',
  'situation familiale',
  'de sexe',
  '(?:hommes?|femmes?) (?:uniquement|seulement|exclusivement)',
  '(?:uniquement|seulement|exclusivement) (?:des )?(?:hommes|femmes)',
  'nationalité',
  'religion',
  'enceinte',
  'état de santé',
  'handicap',
  'photo (?:obligatoire|exigée|requise)',
];
const MOTIF_PROSCRIT = new RegExp(`(?<![\\p{L}\\d])(?:${PROSCRITS.join('|')})(?![\\p{L}])`, 'giu');

export function expressionsProscrites(texte: string): string[] {
  return [...new Set([...texte.matchAll(MOTIF_PROSCRIT)].map((m) => m[0].toLowerCase()))];
}

/** Ce qui manque avant de figer une version ; liste vide = validation possible. */
export function verifierValidation(saisie: SaisieAnnonce, textes: TextesAnnonce): string[] {
  const erreurs: string[] = [];
  if (saisie.pointsDeVente.length === 0) erreurs.push('Choisir au moins un point de vente.');
  if (saisie.missions.length === 0) erreurs.push('Décrire au moins une mission.');
  for (const langue of saisie.langues) {
    if (!textes[langue]?.trim()) erreurs.push(`Le texte en ${LIBELLES_LANGUE[langue].toLowerCase()} est vide.`);
  }
  const relu = [
    saisie.intitule,
    ...saisie.missions,
    ...saisie.criteres.map((c) => c.libelle),
    saisie.horaires ?? '',
    textes.fr ?? '',
  ].join('\n');
  const proscrites = expressionsProscrites(relu);
  if (proscrites.length > 0) {
    erreurs.push(
      `À retirer : ${proscrites.map((e) => `« ${e} »`).join(', ')}. L’âge, la situation familiale, ` +
        'le sexe, la santé, la nationalité ou la religion ne sont jamais des critères.',
    );
  }
  return erreurs;
}

export interface VersionResume {
  version: number;
  langues: LangueAnnonce[];
  valideeLe: string | null;
  valideePar: string | null;
}

export interface PublicationDetail extends Publication {
  id: string;
}

export interface AnnonceDetail {
  id: string;
  reference: string | null;
  statut: StatutAnnonce;
  familleId: string;
  familleCode: string;
  familleLibelle: string;
  intitule: string;
  /** `detectee` : annonce reconstituée depuis une candidature reçue, à compléter. */
  origine: OrigineAnnonce;
  /** `reseaux_sociaux` : annonce « RS », sans texte long ni critères (lot 6). */
  canal: CanalAnnonce;
  publieeLe: string | null;
  closeLe: string | null;
  baremeId: string | null;
  /** Brouillon en cours s'il existe, sinon dernière version validée, sinon état reconstitué. */
  saisie: SaisieAnnonce;
  textes: TextesAnnonce;
  /** Une version non validée attend : la dernière version validée reste celle qui fait foi. */
  brouillonEnCours: boolean;
  versions: VersionResume[];
  publications: PublicationDetail[];
  candidatures: number;
}

export interface TexteVersion {
  version: number;
  valideeLe: string | null;
  textes: TextesAnnonce;
  saisie: SaisieAnnonce;
}
